// LocaleRouteSync — что делает язык в адресе и кто задаёт язык интерфейса.
//
// Правило одно и оно из решения владельца (см. шапку `lib/locale-url.ts`):
// языковой адрес существует ТОЛЬКО для гостя.
//
//   гость на публичной странице с префиксом (`/de/about`) → язык задаёт АДРЕС;
//   гость без префикса, любые шлюзы                       → язык задаёт НАСТРОЙКА;
//   вошедший — ВЕЗДЕ                                      → язык задаёт НАСТРОЙКА,
//                                                           а префикс уходит из адреса.
//
// Отсюда два эффекта, и они не независимы: первый снимает язык с адреса, второй
// возвращает язык интерфейса из настройки. Порознь они дали бы полумеру —
// адрес без префикса, но интерфейс на языке случайной ссылки, или наоборот.
//
// Почему оба здесь, а не в двух компонентах: решение «гость или нет» одно, и
// два подписчика на него неизбежно разъезжаются во времени (кто-то успел, кто-то
// нет). Сам предикат живёт в `lib/locale-url.ts::addressCarriesLocale`.
//
// Что компонент НЕ делает: не сохраняет ничего. Язык из адреса — свойство
// страницы, а не выбор человека; выбор пишет только переключатель
// (`i18n/use-locale.ts::setLocale`).
//
// Гидратация — по `useSession()`, единственному источнику правды о входе в
// приложении. Три состояния, и все три ведут себя предсказуемо:
//
//   о сессии не известно ничего (первый заход, кэша нет) → `isAuthenticated`
//     false → адрес не трогаем. Fail-safe в сторону гостя: снять префикс
//     раньше времени значило бы отобрать языковую версию у того, кто именно
//     на неё и пришёл;
//   сессия поднята из персиста (обычный возврат, в том числе офлайн) →
//     `isAuthenticated` true уже в первом кадре → префикс уходит сразу,
//     мигания адреса нет;
//   персист оказался протухшим и сервер ответил «гость» → префикс к этому
//     моменту уже снят, но снят он на ГОЛЫЙ путь — это и есть штатный адрес
//     страницы (он же `x-default`), поэтому человек остаётся на рабочей
//     странице, а не на 404.
//
// Замена адреса, а не переход: записи в истории не появляется, «назад» ведёт
// туда, откуда человек пришёл.

import { useRouter, useRouterState } from "@tanstack/react-router";
import { useEffect } from "react";

import { useSession } from "@/hooks/use-session";
import i18n, { URL_LOCALE } from "@/i18n";
import { LOCALE_STORAGE_KEY, normalizeLocale, type SupportedLocale } from "@/i18n/locales";
import {
	addressCarriesLocale,
	resolveInterfaceLocale,
	stripLocaleFromAddress,
} from "@/lib/locale-url";

/** Язык, к которому возвращаемся вне публичных языковых адресов. */
function ownLocale(dbLanguage: string | undefined): SupportedLocale {
	try {
		const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
		if (stored) return normalizeLocale(stored);
	} catch {
		// localStorage недоступен (приватный режим) — идём дальше по цепочке.
	}
	if (dbLanguage) return normalizeLocale(dbLanguage);
	return normalizeLocale(typeof navigator === "undefined" ? undefined : navigator.language);
}

export function LocaleRouteSync(): null {
	// Путь ВНУТРЕННИЙ, уже без языкового префикса: его снимает rewrite роутера
	// (`lib/locale-url.ts`). Поэтому здесь достаточно спросить, бывает ли у этого
	// пути языковая версия, — сам префикс переспрашивать не надо.
	const pathname = useRouterState({ select: (s) => s.location.pathname });
	const router = useRouter();
	const { user, isAuthenticated } = useSession();
	const dbLanguage = user?.language;

	// ── Адрес: вошедшему языковой префикс не положен ──────────────────────────
	// `/ru/catalog` → `/catalog`, `/de` → `/`. Заменой, не переходом, и не через
	// `window.history` — подробности в `stripLocaleFromAddress`.
	useEffect(() => {
		if (addressCarriesLocale(isAuthenticated)) return;
		stripLocaleFromAddress(router.history, router.state.location);
	}, [isAuthenticated, router]);

	// ── Язык интерфейса ───────────────────────────────────────────────────────
	useEffect(() => {
		const target = resolveInterfaceLocale({
			urlLocale: URL_LOCALE,
			pathname,
			isAuthenticated,
			own: ownLocale(dbLanguage),
		});
		if (target === null) return;
		if (normalizeLocale(i18n.resolvedLanguage ?? i18n.language) === target) return;
		void i18n.changeLanguage(target);
	}, [pathname, dbLanguage, isAuthenticated]);

	return null;
}
